// DEMS Filing Page - Court Exhibit Management
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { 
  FileText, 
  Plus, 
  Send, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Loader2,
  ChevronRight,
  Files,
  Scale
} from 'lucide-react';
import { format } from 'date-fns';

interface Exhibit {
  id: string;
  caseNumber: string;
  exhibitNumber: string;
  title: string;
  description?: string;
  filingStatus: 'Draft' | 'Pending Review' | 'Submitted' | 'Acknowledged' | 'Filed' | 'Rejected';
  docketNumber?: string;
  courtAcknowledgmentId?: string;
  filedAt?: string;
  acknowledgedAt?: string;
  createdBy: string;
  createdAt: string;
  evidenceCount?: number;
  evidenceItems?: Array<{
    id: string;
    evidenceId: string;
    evidenceTitle?: string;
    fileName?: string;
    evidenceType?: string;
  }>;
}

interface Case {
  caseNumber: string;
  title: string;
}

// Status badge component
function StatusBadge({ status }: { status: Exhibit['filingStatus'] }) {
  const statusConfig = {
    'Draft': { color: 'bg-gray-100 text-gray-700', icon: FileText },
    'Pending Review': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'Submitted': { color: 'bg-blue-100 text-blue-800', icon: Send },
    'Acknowledged': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
    'Filed': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
    'Rejected': { color: 'bg-red-100 text-red-800', icon: AlertCircle },
  };
  
  const config = statusConfig[status] || statusConfig['Draft'];
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.color}`}>
      <Icon className="h-3.5 w-3.5" />
      {status}
    </span>
  );
}

export default function Filing() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCase, setSelectedCase] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedExhibit, setSelectedExhibit] = useState<Exhibit | null>(null);
  const [showAddEvidenceModal, setShowAddEvidenceModal] = useState(false);
  const [selectedEvidenceIds, setSelectedEvidenceIds] = useState<string[]>([]);
  
  // Form state for new exhibit
  const [newExhibit, setNewExhibit] = useState({
    exhibitNumber: '',
    title: '',
    description: '',
  });

  // Fetch cases
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Fetch exhibits for selected case
  const { data: exhibits, isLoading: exhibitsLoading, error: exhibitsError } = useQuery({
    queryKey: ['exhibits', selectedCase],
    queryFn: () => apiClient.getExhibits(selectedCase),
    enabled: !!selectedCase,
  });

  // Fetch case evidence for adding to exhibits
  const { data: caseEvidence } = useQuery({
    queryKey: ['evidence', selectedCase],
    queryFn: () => apiClient.getCaseEvidence(selectedCase),
    enabled: !!selectedCase,
  });

  // Create exhibit mutation
  const createExhibitMutation = useMutation({
    mutationFn: (data: { caseNumber: string; exhibitNumber: string; title: string; description?: string; evidenceIds?: string[] }) =>
      apiClient.createExhibit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits', selectedCase] });
      setShowCreateModal(false);
      setNewExhibit({ exhibitNumber: '', title: '', description: '' });
    },
  });

  // File exhibit mutation
  const fileExhibitMutation = useMutation({
    mutationFn: (exhibitId: string) => apiClient.fileExhibit(exhibitId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits', selectedCase] });
      setSelectedExhibit(null);
    },
  });

  // Add evidence to exhibit mutation
  const addEvidenceMutation = useMutation({
    mutationFn: async ({ exhibitId, evidenceIds }: { exhibitId: string; evidenceIds: string[] }) => {
      for (const evidenceId of evidenceIds) {
        await apiClient.addEvidenceToExhibit(exhibitId, evidenceId);
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['exhibits', selectedCase] });
      // Refresh the selected exhibit details
      if (selectedExhibit) {
        const updated = await apiClient.getExhibit(selectedExhibit.id);
        setSelectedExhibit(updated);
      }
      setShowAddEvidenceModal(false);
      setSelectedEvidenceIds([]);
    },
  });

  // Check if user can manage exhibits
  const canManageExhibits = hasRole(['Attorney', 'CaseManager']);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Court Filing</h1>
          <p className="text-gray-600 mt-1">Create exhibits and file evidence with the court</p>
        </div>
        {canManageExhibits && selectedCase && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Create Exhibit
          </button>
        )}
      </div>

      {/* Case selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Case
        </label>
        <select
          value={selectedCase}
          onChange={(e) => setSelectedCase(e.target.value)}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
        >
          <option value="">Choose a case...</option>
          {cases?.filter(c => c.caseNumber !== 'PUBLIC-SUBMISSION').map((c: Case) => (
            <option key={c.caseNumber} value={c.caseNumber}>
              {c.caseNumber} - {c.title}
            </option>
          ))}
        </select>
      </div>

      {/* Exhibits list */}
      {selectedCase && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-justice-navy" />
              <h2 className="text-lg font-semibold text-gray-900">Exhibits for {selectedCase}</h2>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {exhibitsLoading ? (
              <div className="px-6 py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
                <p className="text-gray-500 mt-2">Loading exhibits...</p>
              </div>
            ) : exhibitsError ? (
              <div className="px-6 py-12 text-center">
                <AlertCircle className="h-12 w-12 text-red-400 mx-auto" />
                <p className="text-red-600 mt-2">Failed to load exhibits</p>
                <p className="text-gray-400 text-sm mt-1">Please try again or contact support</p>
              </div>
            ) : !exhibits?.length ? (
              <div className="px-6 py-12 text-center">
                <Files className="h-12 w-12 text-gray-300 mx-auto" />
                <p className="text-gray-500 mt-2">No exhibits created yet</p>
                {canManageExhibits && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-4 text-justice-gold hover:text-justice-gold/80 font-medium"
                  >
                    Create your first exhibit
                  </button>
                )}
              </div>
            ) : (
              exhibits.map((exhibit: Exhibit) => (
                <div
                  key={exhibit.id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedExhibit(exhibit)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-justice-navy/10 rounded-lg">
                        <FileText className="h-5 w-5 text-justice-navy" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-gray-900">{exhibit.exhibitNumber}</span>
                          <StatusBadge status={exhibit.filingStatus} />
                        </div>
                        <p className="text-sm text-gray-600">{exhibit.title}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {exhibit.evidenceCount || 0} evidence items {exhibit.createdAt && `• Created ${format(new Date(exhibit.createdAt), 'MMM d, yyyy')}`}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                  {exhibit.docketNumber && (
                    <div className="mt-2 ml-14 text-xs text-gray-500">
                      Docket: {exhibit.docketNumber}
                      {exhibit.courtAcknowledgmentId && ` • Ack: ${exhibit.courtAcknowledgmentId}`}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Create Exhibit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Exhibit</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exhibit Number *
                </label>
                <input
                  type="text"
                  value={newExhibit.exhibitNumber}
                  onChange={(e) => setNewExhibit(prev => ({ ...prev, exhibitNumber: e.target.value }))}
                  placeholder="e.g., Exhibit A, Exhibit 1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={newExhibit.title}
                  onChange={(e) => setNewExhibit(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Surveillance Footage Package"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newExhibit.description}
                  onChange={(e) => setNewExhibit(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                />
              </div>

              {caseEvidence && caseEvidence.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Evidence
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    You can add evidence after creating the exhibit
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                    {caseEvidence.length} evidence items available for this case
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => createExhibitMutation.mutate({
                  caseNumber: selectedCase,
                  exhibitNumber: newExhibit.exhibitNumber,
                  title: newExhibit.title,
                  description: newExhibit.description || undefined,
                })}
                disabled={!newExhibit.exhibitNumber || !newExhibit.title || createExhibitMutation.isPending}
                className="px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90 transition-colors disabled:opacity-50"
              >
                {createExhibitMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Create Exhibit'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exhibit Detail Modal */}
      {selectedExhibit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 sticky top-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedExhibit.exhibitNumber}</h3>
                  <p className="text-sm text-gray-600">{selectedExhibit.title}</p>
                </div>
                <StatusBadge status={selectedExhibit.filingStatus} />
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {selectedExhibit.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-1">Description</h4>
                  <p className="text-gray-600">{selectedExhibit.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Case:</span>
                  <span className="ml-2 text-gray-900">{selectedExhibit.caseNumber}</span>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <span className="ml-2 text-gray-900">{selectedExhibit.createdAt ? format(new Date(selectedExhibit.createdAt), 'MMM d, yyyy h:mm a') : 'N/A'}</span>
                </div>
                {selectedExhibit.docketNumber && (
                  <div>
                    <span className="text-gray-500">Docket #:</span>
                    <span className="ml-2 text-gray-900 font-mono">{selectedExhibit.docketNumber}</span>
                  </div>
                )}
                {selectedExhibit.courtAcknowledgmentId && (
                  <div>
                    <span className="text-gray-500">Court Ack:</span>
                    <span className="ml-2 text-gray-900 font-mono">{selectedExhibit.courtAcknowledgmentId}</span>
                  </div>
                )}
                {selectedExhibit.filedAt && (
                  <div>
                    <span className="text-gray-500">Filed:</span>
                    <span className="ml-2 text-gray-900">{format(new Date(selectedExhibit.filedAt), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                )}
                {selectedExhibit.acknowledgedAt && (
                  <div>
                    <span className="text-gray-500">Acknowledged:</span>
                    <span className="ml-2 text-gray-900">{format(new Date(selectedExhibit.acknowledgedAt), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                )}
              </div>

              {/* Evidence items would go here */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Evidence Items</h4>
                  {canManageExhibits && selectedExhibit.filingStatus === 'Draft' && (
                    <button
                      onClick={() => setShowAddEvidenceModal(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-justice-gold hover:bg-justice-gold/10 rounded transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Evidence
                    </button>
                  )}
                </div>
                {selectedExhibit.evidenceItems?.length ? (
                  <div className="space-y-2">
                    {selectedExhibit.evidenceItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Files className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{item.evidenceTitle || item.fileName}</span>
                        <span className="text-xs text-gray-400">{item.evidenceType}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No evidence items added yet</p>
                )}
              </div>

              {/* Filing action info */}
              {selectedExhibit.filingStatus === 'Submitted' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Awaiting Court Acknowledgment</p>
                      <p className="text-sm text-blue-700 mt-1">
                        The exhibit has been submitted to the court and is pending acknowledgment.
                        This typically takes a few minutes.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedExhibit.filingStatus === 'Acknowledged' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">Court Acknowledged</p>
                      <p className="text-sm text-green-700 mt-1">
                        The court has acknowledged receipt of this exhibit. 
                        Court Acknowledgment ID: <span className="font-mono">{selectedExhibit.courtAcknowledgmentId}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center sticky bottom-0">
              <button
                onClick={() => setSelectedExhibit(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
              
              {canManageExhibits && (selectedExhibit.filingStatus === 'Draft' || selectedExhibit.filingStatus === 'Rejected') && (
                <button
                  onClick={() => fileExhibitMutation.mutate(selectedExhibit.id)}
                  disabled={fileExhibitMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-justice-gold text-white rounded-lg hover:bg-justice-gold/90 transition-colors disabled:opacity-50"
                >
                  {fileExhibitMutation.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      File with Court
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Evidence Modal */}
      {showAddEvidenceModal && selectedExhibit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Add Evidence to {selectedExhibit.exhibitNumber}</h3>
              <p className="text-sm text-gray-500 mt-1">Select evidence items to include in this exhibit</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {caseEvidence && caseEvidence.length > 0 ? (
                <div className="space-y-2">
                  {caseEvidence
                    .filter((e: any) => !selectedExhibit.evidenceItems?.some(ei => ei.evidenceId === e.id))
                    .map((evidence: any) => (
                      <label
                        key={evidence.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedEvidenceIds.includes(evidence.id)
                            ? 'border-justice-gold bg-justice-gold/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEvidenceIds.includes(evidence.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEvidenceIds(prev => [...prev, evidence.id]);
                            } else {
                              setSelectedEvidenceIds(prev => prev.filter(id => id !== evidence.id));
                            }
                          }}
                          className="w-4 h-4 text-justice-gold border-gray-300 rounded focus:ring-justice-gold"
                        />
                        <Files className="h-4 w-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{evidence.title || evidence.fileName}</p>
                          <p className="text-xs text-gray-500">{evidence.type} • {evidence.id}</p>
                        </div>
                      </label>
                    ))}
                  {caseEvidence.filter((e: any) => !selectedExhibit.evidenceItems?.some(ei => ei.evidenceId === e.id)).length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">All evidence items are already in this exhibit</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No evidence items available for this case</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => { setShowAddEvidenceModal(false); setSelectedEvidenceIds([]); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => addEvidenceMutation.mutate({ exhibitId: selectedExhibit.id, evidenceIds: selectedEvidenceIds })}
                disabled={selectedEvidenceIds.length === 0 || addEvidenceMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90 transition-colors disabled:opacity-50"
              >
                {addEvidenceMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-5 w-5" />
                    Add {selectedEvidenceIds.length} Item{selectedEvidenceIds.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
